import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { TeamMemberVolumeStat } from '@/api/type-response'
import { Loading, StorageCard, Trans } from '@/components'
import { Pagination } from '@/components/base/Pagination'
import { ORDER_CATALOG, OrderStatus } from '@/consts/order'
import { TeamRole } from '@/consts/team'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { formatDatePreset } from '@/utils/formatter'
import { formatStorageGB, getEffectiveQuota } from '@/utils/storage'

import { MemberFilter } from '../../components/MemberFilter'
import { TeamStorageUpgradeModal } from '../components/TeamStorageUpgradeModal'
import { ViewSwitcher } from '../components/ViewSwitcher'

export function AdminSpaceView() {
  const [loading, setLoading] = useState(false)
  const [volumeStats, setVolumeStats] = useState<TeamMemberVolumeStat[]>([])
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [teamSharedUsage, setTeamSharedUsage] = useState(0)
  const [teamSharedQuota, setTeamSharedQuota] = useState(0)
  const [allMembersTotalUsage, setAllMembersTotalUsage] = useState(0)
  const [allMembersTotalQuota, setAllMembersTotalQuota] = useState(0)
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false)
  const [currentAutoRenewal, setCurrentAutoRenewal] = useState(false)

  const pageSize = 10
  const teamStore = useTeamStore()
  const navigate = useNavigate()
  const userRole = useTeamUserRole()
  const isOwner = userRole === TeamRole.Owner

  const teamTokenData = teamStore.teamTokenData

  const loadTeamStorageData = async () => {
    if (!teamTokenData?.teamResID) return

    try {
      // Get usage stats
      const res = await API.getTeamStatStorages({ res_id: teamTokenData.teamResID })
      setTeamSharedUsage(res.teamSharedStorageSpaceOverview.usageBytes)
      setAllMembersTotalUsage(res.memberStorageSpaceOverview.usageBytes)
      setAllMembersTotalQuota(res.memberStorageSpaceOverview.quotaBytes)

      // Get team info (quota + expireTime + autoRenewal)
      const teamInfoRes = await API.getTeamInfo({ res_id: teamTokenData.teamResID })
      const volumes = teamInfoRes?.team?.volumes

      // Fetch team shared storage order (catalog=9) for auto renewal status
      const orderRes = await API.getOrders({
        catalog: ORDER_CATALOG.TEAM_SHARED_STORAGE,
        status_filter: OrderStatus.ALL,
      })
      const orders = orderRes?.orders || []
      const latestOrder = orders[orders.length - 1]
      if (latestOrder) {
        setCurrentAutoRenewal(latestOrder.autoRenewal || false)
      }

      if (volumes) {
        // Check expiration from volumes.expireTime
        let expired = false
        if (volumes.expireTime?.seconds) {
          expired = volumes.expireTime.seconds * 1000 < Date.now()
        }

        // Calculate effective quota
        if (volumes.allocPart) {
          const effectiveQuotaGB = getEffectiveQuota(volumes.allocPart, expired)
          setTeamSharedQuota(effectiveQuotaGB * 1024 * 1024 * 1024)
        }
      }
    } catch (error) {
      console.error('Failed to load team storage data:', error)
    }
  }

  const fetchVolumeStats = async (p = page, filterMembers = selectedMembers) => {
    if (!teamTokenData?.teamResID) return

    setLoading(true)
    try {
      const res = await API.getTeamMemberVolumeStats({
        res_id: teamTokenData.teamResID,
        page: p,
        per_page: pageSize,
        member_res_ids: filterMembers.length > 0 ? filterMembers : undefined,
      })

      if (res.volumes) {
        setVolumeStats(res.volumes)
        setPage(p)
        setTotal(res.pagination?.total || 0)
      }
    } catch (error) {
      console.error('Failed to fetch volume stats:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTeamStorageData()
    fetchVolumeStats(1, [])
  }, [])

  useEffect(() => {
    fetchVolumeStats(1, selectedMembers)
  }, [selectedMembers])

  const handleDetailsClick = (stat: TeamMemberVolumeStat) => {
    navigate(`/team/space/member/${stat.user.resID}`, {
      state: {
        memberName: stat.user.user.nickname || stat.user.user.name,
      },
    })
  }

  return (
    <div className="card bg-panel-white p-8 flex flex-col gap-8 h-full overflow-y-auto">
      {/* Section 1: Quota + ViewSwitcher */}
      <div className="border-b border-neutral-100 pb-4">
        <div className="flex items-center gap-2">
          <div className="h6">
            <Trans i18nKey="space.titleQuota">Quota</Trans>
          </div>
          <ViewSwitcher />
        </div>
      </div>

      {/* Section 2: Storage Overview Cards */}
      <div className="grid grid-cols-2 gap-4">
        <StorageCard
          title={<Trans i18nKey="teamSpace.admin.teamCardTitle">Team Storage Space</Trans>}
          usage={teamSharedUsage}
          quota={teamSharedQuota}
          colorVariant="tertiary"
          legendText={<Trans i18nKey="teamSpace.admin.usedSharedSpace">Used Shared Space</Trans>}
          actions={
            <>
              <Link to="/team/space/shared" className="text-highlight t-sb">
                <Trans i18nKey="teamSpace.admin.manageLink">Manage</Trans>
              </Link>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => setUpgradeModalOpen(true)}
                  className="text-highlight t-sb cursor-pointer"
                >
                  <Trans i18nKey="teamSpace.admin.upgrade">Upgrade</Trans>
                </button>
              )}
            </>
          }
        />
        <StorageCard
          title={
            <Trans i18nKey="teamSpace.admin.allMembersCardTitle">
              Team Storage Space (All Members)
            </Trans>
          }
          usage={allMembersTotalUsage}
          quota={allMembersTotalQuota}
          colorVariant="secondary"
          legendText={<Trans i18nKey="teamSpace.admin.usedSpace">Used Space</Trans>}
        />
      </div>

      {/* Section 3: Member Filter + Table */}
      <div className="flex flex-col gap-4">
        {/* Filters */}
        <div>
          <MemberFilter value={selectedMembers} onChange={setSelectedMembers} />
        </div>

        {/* Table */}
        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : volumeStats.length === 0 ? (
          <div className="w-full flex flex-col items-center justify-center p-12">
            <div className="t-sb text-secondary">
              <Trans i18nKey="space.noVolumeDataFound">No volume data found</Trans>
            </div>
          </div>
        ) : (
          <>
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="text-left rounded-tl-lg">
                    <span className="t-sb text-secondary">
                      <Trans i18nKey="space.tableHeaderID">ID</Trans>
                    </span>
                  </th>
                  <th className="text-left">
                    <span className="t-sb text-secondary">
                      <Trans i18nKey="space.tableHeaderCreator">Creator</Trans>
                    </span>
                  </th>
                  <th className="text-left">
                    <span className="t-sb text-secondary">
                      <Trans i18nKey="space.tableHeaderDatadrive">Datadrive</Trans>
                    </span>
                  </th>
                  <th className="text-left">
                    <span className="t-sb text-secondary">
                      <Trans i18nKey="space.tableHeaderExpirationTime">Expiration Time</Trans>
                    </span>
                  </th>
                  <th className="text-left rounded-tr-lg">
                    <span className="t-sb text-secondary">
                      <Trans i18nKey="space.tableHeaderAction">Action</Trans>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {volumeStats.map((stat) => (
                  <tr key={stat.user.resID}>
                    <td className="t-sm">
                      <div className="t-sb">{stat.user.resID}</div>
                    </td>
                    <td className="t-sm">
                      <div className="t-sb">{stat.user.user.nickname || stat.user.user.name}</div>
                    </td>
                    <td className="t-sm">
                      <div className="t-sb">
                        {formatStorageGB(stat.fsSubPart.usageBytes).replace(/ GB$/, '')}
                        <span className="text-tertiary"> / {stat.fsSubPart.quotaGB} GB</span>
                      </div>
                    </td>
                    <td className="t-sm">
                      <div className="t-sb">
                        {stat.storageStat?.expireTime
                          ? formatDatePreset(stat.storageStat.expireTime.seconds * 1000)
                          : '-'}
                      </div>
                    </td>
                    <td className="t-sm">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-highlight t-sb hover:underline cursor-pointer"
                          onClick={() => handleDetailsClick(stat)}
                        >
                          <Trans i18nKey="space.actionDetails">Details</Trans>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              size={pageSize}
              total={total}
              current={page}
              className="justify-end mt-4"
              onChange={(newPage) => fetchVolumeStats(newPage, selectedMembers)}
            />
          </>
        )}
      </div>

      <TeamStorageUpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        onSuccess={() => {
          setUpgradeModalOpen(false)
          loadTeamStorageData()
        }}
        initialAutoRenewal={currentAutoRenewal}
      />
    </div>
  )
}
